import { PageLoader } from '@setvi/shared/components';
import { CompanyProductUserType } from '@setvi/shared/services/react-query/query/user/types';
import { Box } from '@material-ui/core';
import { Customer } from 'Services/Mongo/Types';
import {
  CatalogConfig,
  PriceGroup,
  Product,
  ProductDetailsTabsIds
} from '@setvi/shared/interfaces';
import { DetailsContent } from './content';
import { useProductDetails } from './hooks';
import { useSalesKitStore } from './store';
import { useAskAIStorage } from './content/components/ask-ai/store';
import { ProductDetailsEmptyState } from './empty-state';

export interface DetailsDialogProps {
  selectedProduct: Partial<Product>;
  selectedProducts?: Product[];
  selectedCustomerId?: Customer['_id'];
  selectedPriceGroup?: PriceGroup;
  defaultOpenedTab?: ProductDetailsTabsIds;
  userType?: CompanyProductUserType;
  readOnly?: boolean;
  canSelectProducts?: boolean;
  selectionType?: 'none' | 'checkbox' | 'radio';
  hideProductPrice?: boolean;
  priceLoading?: boolean;
  configOverride?: CatalogConfig;
  orderHistoryValues?: {
    accountId: string;
    addressId?: string;
    infoMessage?: string;
    customerName?: string;
    showShipToButton?: boolean;
  };
  disableStockRefresh?: boolean;
  onClose: () => void;
  handleProductSelect?: (isChecked: boolean, product: Product[]) => void;
  updateProduct?: (product: Product, newValues: Partial<Product>) => void;
  captureResourceAnalytics?: (additionalValues?: {
    [key: string]: string | number;
  }) => void;
  openProductDetails?: (
    product: any,
    defaultOpenedTab?: ProductDetailsTabsIds
  ) => void;
  handleEmail?: () => void;
  handleSelected?: () => void;
  handleCloseActionBar?: () => void;
}

export const ProductDetails = ({
  selectedProduct,
  selectedProducts,
  selectedCustomerId,
  selectedPriceGroup,
  orderHistoryValues,
  defaultOpenedTab,
  userType,
  readOnly = false,
  canSelectProducts,
  selectionType,
  priceLoading,
  hideProductPrice = false,
  configOverride,
  disableStockRefresh,
  handleProductSelect,
  onClose,
  updateProduct,
  captureResourceAnalytics,
  openProductDetails,
  handleEmail,
  handleSelected,
  handleCloseActionBar
}: DetailsDialogProps) => {
  const {
    product,
    customer,
    isLoading,

    updateDialogProduct
  } = useProductDetails({
    selectedProduct,
    selectedCustomerId,
    userType
  });

  const { resetSalesKitStore } = useSalesKitStore();
  const { reset } = useAskAIStorage();

  const triggerClose = () => {
    onClose();
    resetSalesKitStore();
    reset();
  };

  if (!selectedProduct) return null;

  return (
    <Box
      tabIndex={0}
      overflow="hidden"
      display="flex"
      flex={1}
      style={{ background: '#F8F9FA' }}>
      {isLoading ? (
        <PageLoader />
      ) : (
        <>
          {product ? (
            <DetailsContent
              product={product}
              selectedCustomer={customer}
              selectedPriceGroup={selectedPriceGroup}
              readOnly={readOnly}
              defaultOpenedTab={defaultOpenedTab}
              selectedProducts={selectedProducts}
              canSelectProducts={canSelectProducts}
              selectionType={selectionType}
              hideProductPrice={hideProductPrice}
              configOverride={configOverride}
              orderHistoryValues={orderHistoryValues}
              priceLoading={priceLoading}
              handleProductSelect={handleProductSelect}
              disableStockRefresh={disableStockRefresh}
              captureResourceAnalytics={captureResourceAnalytics}
              updateProduct={(prod, newValues) => {
                updateDialogProduct(newValues);
                updateProduct?.(prod, newValues);
              }}
              openProductDetails={openProductDetails}
              closeDialog={triggerClose}
              handleEmail={handleEmail}
              handleSelected={handleSelected}
              handleCloseActionBar={handleCloseActionBar}
            />
          ) : (
            <ProductDetailsEmptyState onClose={onClose} />
          )}
        </>
      )}
    </Box>
  );
};
